import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import * as Dropzone from 'react-dropzone';
import { Field } from '@/isomorphic/components/forms/components/field';
import { ValidatorType, useValidation } from '@/hooks/use-validation';
import { Button } from '@/isomorphic/components/typography/button/button';
import { AppContext } from '@/contexts/app-context';
import { Checkbox } from '@/isomorphic/components/forms/components/checkbox';
import { Select } from '@/isomorphic/components/forms/components/select';
import { useLocalization } from '@/hooks/use-localization';
import { GoogleRecaptchaInfo } from '../../../google-recaptcha-info/google-recaptcha-info';
import { Loading } from '@/isomorphic/components/loading/loading';
import clsx from 'clsx';
import { FormRendererProps, FormValues } from './types';
import { useFileUpload } from './use-file-upload';
import { useFormSubmit } from './use-form-submit';

export const FormRenderer = ({
    parsedFields,
    formSettings,
    onBeforeSubmit,
    onSuccess,
    getSuccessContent,
    getFieldValidator,
    dataComponent,
    submissionRoute,
    includeB2bConfirmed,
    onSubmit,
}: FormRendererProps) => {
    const { locale } = useContext(AppContext);
    const { getValidatorByInputName } = useValidation();
    const { localize } = useLocalization();

    const [formValues, setFormValues] = useState<FormValues>({});
    const [isClient, setIsClient] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const formRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [contentMinHeight, setContentMinHeight] = useState<number>(0);

    // Filter out misconfigured select fields (no options or no label)
    const validFields = useMemo(
        () =>
            parsedFields.filter(field => {
                if (field.type === 'select' && (!field.list || field.list.length === 0 || !field.label)) {
                    return false;
                }
                return true;
            }),
        [parsedFields]
    );

    const { files, onDrop, removeFile, maxFileSize } = useFileUpload();
    const { loading, error, finished, submitResult, handleSubmit } = useFormSubmit({
        formId: formSettings.formId || '',
        submissionRoute,
        onBeforeSubmit,
        onSuccess,
        generalSubmitErrorMessage: formSettings.generalSubmitErrorMessage,
        includeB2bConfirmed,
        onSubmit,
    });

    useEffect(() => {
        setIsClient(true);
    }, []);

    // Capture content height before transitioning to finished state so the thank you message keeps the same size
    useEffect(() => {
        if (!finished && contentRef.current) {
            setContentMinHeight(contentRef.current.offsetHeight);
        }
    }, [finished]);

    const getRemoveText = () => {
        switch (locale) {
            case 'sv-se':
                return 'Ta bort';
            case 'da-dk':
            case 'nb-no':
                return 'Fjerne';
            case 'fi-fi':
                return 'Poista';
            default:
                return 'Remove';
        }
    };

    const handleChange = (fieldName: string, newValue: string | boolean) => {
        setFormValues(prevState => ({ ...prevState, [fieldName]: newValue }));
    };

    const getValidatorConfig = (fieldName: string) => {
        const customConfig = getFieldValidator?.(fieldName, formValues);
        if (customConfig) {
            return customConfig;
        }
        // Fall back to default validator
        return { validator: getValidatorByInputName(fieldName as ValidatorType) };
    };

    const validateForm = () => {
        setIsFormSubmitted(true);
        let firstInvalidField: string | null = null;

        const isValid = validFields.every(field => {
            if (!field.fieldName) {
                return true;
            }

            const fieldName = field.fieldName || '';
            const fieldValue = formValues[fieldName] || '';

            const setInvalid = () => {
                if (!firstInvalidField) {
                    firstInvalidField = fieldName;
                }
                return false;
            };

            if (field.required && !fieldValue) {
                return setInvalid();
            }

            if (fieldName === 'files') {
                if (field.required && !files.length) {
                    return setInvalid();
                }
                return true;
            }

            // Only validate if field has a value (optional empty fields should pass)
            const { validator } = getValidatorConfig(fieldName);
            if (fieldValue && validator && !validator(String(fieldValue))) {
                return setInvalid();
            }

            return true;
        });

        if (!isValid) {
            if (firstInvalidField && formRef.current) {
                const htmlName = (firstInvalidField as string).replace(/[^a-z0-9-_]+/g, '').toLocaleLowerCase();
                formRef.current.querySelector<HTMLElement>(`#${htmlName}`)?.focus();
            }
            return;
        }

        handleSubmit(formValues, files);
    };

    const getSuccessTitle = () => {
        if (getSuccessContent) {
            return getSuccessContent(submitResult).title;
        }
        return formSettings.successTitle || '';
    };

    const getSuccessMessage = () => {
        if (getSuccessContent) {
            return getSuccessContent(submitResult).message;
        }
        return formSettings.successMessage || '';
    };

    const renderField = (field: (typeof parsedFields)[0], index: number) => {
        const fieldName = field.fieldName || '';
        const fieldValue = formValues[fieldName] || '';
        const validatorConfig = getValidatorConfig(fieldName);

        switch (field.type) {
            case 'input':
                return (
                    <Field
                        key={`${fieldName}--${index}`}
                        placeholder={field.label}
                        value={fieldValue as string}
                        {...field}
                        // errorMessage must come after {...field} so a dynamically computed
                        // message (e.g. loanAmount min vs generic) is not clobbered by the
                        // field's static errorMessage spread above.
                        errorMessage={validatorConfig.errorMessage || field.errorMessage || localize('input-field-default-error')}
                        onChange={value => handleChange(fieldName, value)}
                        validation={validatorConfig.validator}
                        showRequiredError={isFormSubmitted && field.required && !fieldValue}
                        darkMode={formSettings.darkMode}
                    />
                );

            case 'textarea':
                return (
                    <Field
                        key={`${fieldName}--${index}`}
                        placeholder={field.label}
                        required={field.required}
                        value={fieldValue as string}
                        {...field}
                        // errorMessage must come after {...field} (see input case above).
                        errorMessage={validatorConfig.errorMessage || field.errorMessage || localize('input-field-default-error')}
                        onChange={value => handleChange(fieldName, value)}
                        validation={validatorConfig.validator}
                        showRequiredError={isFormSubmitted && field.required && !fieldValue}
                        darkMode={formSettings.darkMode}
                    />
                );

            case 'checkbox':
                return (
                    <Checkbox
                        key={`${fieldName}--${index}`}
                        {...field}
                        type="checkbox"
                        checked={fieldValue as boolean}
                        onChange={checked => handleChange(fieldName, checked)}
                        showRequiredError={isFormSubmitted && field.required && !fieldValue}
                        errorMessage={field.errorMessage || localize('input-field-default-error')}
                        darkMode={formSettings.darkMode}
                    />
                );

            case 'select':
                return (
                    <Select
                        key={`${fieldName}--${index}`}
                        label={field.label}
                        options={field.list!.map((item: { name: string }) => ({
                            label: item.name,
                            value: item.name,
                        }))}
                        {...field}
                        value={(fieldValue as string) ?? ''}
                        onChange={value => handleChange(fieldName, String(value))}
                        showRequiredError={isFormSubmitted && field.required && !fieldValue}
                        errorMessage={field.errorMessage || localize('input-field-default-error')}
                        darkMode={formSettings.darkMode}
                    />
                );

            case 'file':
                return isClient ? (
                    <Dropzone.default key={`${fieldName}--${index}`} onDrop={onDrop} maxSize={maxFileSize}>
                        {({ getRootProps, getInputProps, isDragActive }) => (
                            <div>
                                <p className="r-text-sm r-font-bold">{localize(fieldName)}</p>
                                {files.length > 0 && (
                                    <div className="r-py-1">
                                        <ul className="r-space-y-2">
                                            {files.map((acceptedFile: FileWithPath) => (
                                                <li key={acceptedFile.name}>
                                                    <div className="r-flex r-items-center r-justify-between">
                                                        <p>{acceptedFile.path}</p>
                                                        <button
                                                            className="r-rounded-full r-bg-danger r-p-2 r-text-sm r-text-white"
                                                            onClick={() => removeFile(acceptedFile.name)}
                                                        >
                                                            {getRemoveText()}
                                                        </button>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <section>
                                    <div
                                        {...getRootProps()}
                                        className={`r-flex r-w-full r-cursor-pointer r-justify-center r-rounded-md r-border-2 r-border-dashed r-border-[#999] r-p-9 r-transition-colors r-duration-300 hover:r-bg-gray-300 ${
                                            isDragActive && 'r-bg-gray-300'
                                        }`}
                                    >
                                        <input {...getInputProps()} />
                                        <p>{field.dropText || 'Click or drag here to add files'}</p>
                                    </div>
                                </section>
                            </div>
                        )}
                    </Dropzone.default>
                ) : null;

            default:
                return null;
        }
    };

    return (
        <div
            ref={formRef}
            data-component={dataComponent}
            className={clsx('r-flex r-max-w-xl r-flex-col r-gap-4', {
                'dark-mode': formSettings.darkMode || false,
            })}
        >
            <div
                ref={contentRef}
                className="r-flex r-flex-col r-gap-4"
                style={finished && contentMinHeight > 0 ? { minHeight: contentMinHeight } : undefined}
            >
                {finished ? (
                    <div className={clsx('r-flex r-flex-1 r-flex-col r-justify-center r-space-y-4', { 'r-text-white': formSettings.darkMode })}>
                        {getSuccessTitle() && <p className="r-text-4xl r-font-bold">{getSuccessTitle()}</p>}
                        <p>{getSuccessMessage()}</p>
                    </div>
                ) : (
                    <>
                        <div className="r-relative">
                            {loading && (
                                <div className="r-absolute r-inset-0 r-z-10 r-flex r-items-center r-justify-center">
                                    <Loading />
                                </div>
                            )}
                            <div
                                className={clsx('r-flex r-flex-col r-gap-4', { 'r-pointer-events-none r-select-none': loading })}
                                {...(loading ? { inert: '' } : {})}
                            >
                                {validFields.map((field, index) => renderField(field, index))}
                            </div>
                        </div>

                        {error ? (
                            <div role="alert" className="r-row-start-4 r-mt-2 r-bg-red-700 r-p-4 r-text-sm r-text-white">
                                {error}
                            </div>
                        ) : null}
                        <GoogleRecaptchaInfo darkMode={formSettings.darkMode || false} />
                        <Button type={formSettings.buttonType || 'primary'} disabled={loading} onClick={validateForm}>
                            {localize('submit')}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};
